import type * as THREE from 'three'
import { UnsupportedGraphicsCapabilityError } from '@/graphics/GraphicsErrors'
import { GSplatFlipbookAssetCache } from '@/plugins/gsplat/animation/GSplatFlipbookAssetCache'
import {
  GSplatFlipbookController,
  type GSplatFlipbookOptions,
} from '@/plugins/gsplat/animation/GSplatFlipbookController'
import type { SplatData } from '@/plugins/gsplat/core/SplatData'
import {
  GSplatRelightingLayerRegistry,
  GSplatRelightingViewController,
  type GSplatRelightingViewControllerOptions,
} from '@/plugins/gsplat/effects/relighting/GSplatRelightingViewController'
import type { GSplatTrees } from '@/plugins/gsplat/effects/trees/GSplatTrees'
import {
  attachGSplatTrees,
  type GSplatTreesAttachment,
  type GSplatTreesPlacementTarget,
} from '@/plugins/gsplat/effects/trees/GSplatTreesGpuResources'
import {
  GSplatWeatherController,
  type GSplatWeatherOptions,
  type GSplatWeatherRuntime,
} from '@/plugins/gsplat/effects/weather/GSplatWeather'
import {
  type GSplatProcessingSlot,
  GSplatProcessor,
  type GSplatProcessorInvalidation,
  type GSplatProcessorOptions,
} from '@/plugins/gsplat/GSplatProcessor'
import { GSplatImage, type GSplatImageOptions } from '@/plugins/gsplat/generators/GSplatImage'
import { GSplatLines, type GSplatLinesOptions } from '@/plugins/gsplat/generators/GSplatLines'
import { GSplatMesh, type GSplatMeshOptions } from '@/plugins/gsplat/generators/GSplatMesh'
import { GSplatText, type GSplatTextOptions } from '@/plugins/gsplat/generators/GSplatText'
import type { GSplatGraphicsAdapter } from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import type { GSplatStreamDescriptor } from '@/plugins/gsplat/lod/GSplatFormat'
import {
  StreamedGSplat,
  type StreamedGSplatOptions,
} from '@/plugins/gsplat/orchestration/StreamedGSplat'
import {
  GSplatAssetLoadingService,
  type GSplatCompatibilityMode,
  type GSplatCompatibilityProvider,
} from '@/plugins/gsplat/system/GSplatAssetLoadingService'
import { GSplatAssetStore } from '@/plugins/gsplat/system/GSplatAssetStore'
import { GSplatFrameService } from '@/plugins/gsplat/system/GSplatFrameService'
import { GSplatHandleCommandService } from '@/plugins/gsplat/system/GSplatHandleCommandService'
import { GSplatPickingService } from '@/plugins/gsplat/system/GSplatPickingService'
import type { GSplatDebugMode } from '@/plugins/gsplat/unified/GSplatParams'
import type { GSplatResolvedSortingMode } from '@/plugins/gsplat/unified/GSplatPipelineTypes'
import type { GSplatResourceCreateContext } from '@/plugins/gsplat/unified/resource/GSplatResourceFactory'
import { ExporterRegistry } from '@/runtime/ExporterRegistry'
import type {
  AssetExporter,
  AssetLoader,
  ExportOptions,
  LoadContext,
  PhaseRenderContext,
} from '@/shared/types/assets'
import type {
  AssetId,
  ClipVolume,
  Disposable,
  PickQuery,
  PickResult,
  RectPickQuery,
} from '@/shared/types/core'
import type {
  GSplatDeviceBudgetDiagnostics,
  GSplatVisualActivitySnapshot,
  GSplatWorkBufferUpdateMode,
  LODConfig,
  LODStats,
} from '@/shared/types/gsplat'
import type { GSplatCapability } from '@/shared/types/gsplatCapability'
import type {
  EngineFrameContext,
  ViewFrameContext,
  ViewId,
  ViewSnapshot,
} from '@/shared/types/view'
import { assertGSplatGpuSortingRequirements } from './GSplatCapabilityRequirements'
import { assertGSplatExtraExporterCapability } from './GSplatExtraExporterCapability'
import type {
  GSplatAssetLodConfiguration,
  GSplatResourceLease,
  IGSplatHandleBinding,
  IGSplatHandleBindingFactory,
} from './GSplatHandleBinding'
import type {
  GaussianSplatExtraExporter,
  GaussianSplatHandle,
  GaussianSplatLoadRequest,
  GaussianSplatRenderConfig,
} from './types/PluginTypes'

export type {
  GSplatAssetLodConfiguration,
  GSplatResourceLease,
  IGSplatHandleBinding,
  IGSplatHandleBindingFactory,
} from './GSplatHandleBinding'

/** Exporter 可读取公开 Handle，也可读取 format-specific CPU 数据出口。 */
export interface GSplatExportSource {
  readonly handle: GaussianSplatHandle
  readonly resource: GSplatResourceLease
  readonly cpuData: unknown | null
  readonly resourceKind: NonNullable<GSplatResourceLease['resourceKind']>
  readonly streaming: NonNullable<GSplatResourceLease['streaming']>
  readonly octreeSemantics: NonNullable<GSplatResourceLease['octreeSemantics']>
}

export interface GSplatSystemConfiguration {
  readonly capabilities: GSplatCapability
  getConfig(): GaussianSplatRenderConfig
  getResolvedSortingMode(): GSplatResolvedSortingMode
  setConfig(config: Partial<GaussianSplatRenderConfig>): void
}

/** StreamedGSplat 对齐 PlayCanvas scene.gsplat 的全局 streaming 配置子集。 */
export interface GSplatStreamingConfiguration {
  readonly radialSorting: boolean
  readonly lodUpdateAngle: number
  readonly lodBehindPenalty: number
  readonly lodUpdateDistance: number
  readonly lodUnderfillLimit: number
  readonly lodRefinementMode?: 'step' | 'direct'
}

/** 唯一 Unified Params/Director 向 System 提供的 runtime 控制面。 */
export interface GSplatSystemRuntimeControl {
  prewarmClip?(maxVertices: number, maxVolumes?: number): boolean
  releaseClipPrewarm?(): boolean
  getStreamingConfiguration(): GSplatStreamingConfiguration
  setStreamingConfiguration(config: Readonly<GSplatStreamingConfiguration>): void
  getDebugMode(): GSplatDebugMode
  setDebugMode(mode: GSplatDebugMode): void
  getRenderedSplatCount(): number
}

/**
 * Plugin host API 在迁移期需要的 System-owned 服务面。
 *
 * 当前 WebGL 组合可委托给冻结的 PlayCanvas compatibility 实现；Unified 迁移完成后由 Params、World 与
 * asset services 直接实现。Plugin 不得借此重新持有 Manager、registry 或 backend 分支。
 */
export interface GSplatSystemHostServices extends Disposable {
  setClipVolumes(volumes: ReadonlyArray<ClipVolume>): boolean
  setLodConfig(config: Partial<LODConfig>): void
  getLodConfig(): LODConfig
  getLodStats(): LODStats
  getDeviceBudgetDiagnostics(): GSplatDeviceBudgetDiagnostics
  getVisualActivitySnapshot(): GSplatVisualActivitySnapshot | undefined
  /** 原子发布永久 WorkBuffer extra stream，并同步现存 Unified Manager。 */
  ensureExtraStreams?(streams: readonly GSplatStreamDescriptor[]): boolean
}

/** RenderPipeline MainTransparent 内由 System 选择的提交实现。 */
export interface GSplatSystemRenderSubmission extends Disposable {
  render(
    frame: ViewFrameContext,
    context: PhaseRenderContext,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): void
}

export type GSplatSystemOwner = object | symbol | string

export interface GSplatStreamingConfigurationLease extends Disposable {
  readonly owner: GSplatSystemOwner
  set(config: Readonly<GSplatStreamingConfiguration>): void
}

export interface GSplatDebugLease extends Disposable {
  readonly owner: GSplatSystemOwner
  set(mode: GSplatDebugMode): void
}

/** System 全局配置或 debug owner 冲突。 */
export class GSplatSystemOwnerConflictError extends Error {
  readonly code = 'GSPLAT_SYSTEM_OWNER_CONFLICT'

  constructor(readonly domain: 'streaming-configuration' | 'debug') {
    super(`GSplatSystem ${domain} 已由另一个 owner 持有`)
    this.name = new.target.name
  }
}

export interface GSplatSystemAssetSnapshot {
  readonly id: AssetId
  readonly generation: number
  readonly handle: GaussianSplatHandle
  readonly resource: GSplatResourceLease
  readonly mounted: boolean
}

export type GSplatViewRetireReason =
  | 'removed'
  | 'camera-replaced'
  | 'array-camera'
  | 'system-dispose'

export interface GSplatViewLifecycleContext {
  readonly generation: number
}

/**
 * PlayCanvas GSplatDirector/Manager 图在本地引擎中的 System-owned 入口。
 * 实现只能消费共享领域对象，不能按 graphics kind 创建另一套 Manager。
 */
export interface GSplatSystemDirector extends Disposable {
  updateStreaming(frame: EngineFrameContext, assets: readonly GSplatSystemAssetSnapshot[]): void
  prepareView(
    frame: ViewFrameContext,
    assets: readonly GSplatSystemAssetSnapshot[],
    lifecycle: GSplatViewLifecycleContext
  ): void
  suspendView(viewId: ViewId, generation: number): void
  retireView(viewId: ViewId, generation: number, reason: GSplatViewRetireReason): void
  /** ViewId 是 material owner key；同一 Camera 的多个 View 不得合并。 */
  getMaterial(viewId: ViewId, layerId: number): unknown | null
}

/** CPU picker 只读取 Resource/Placement 快照，不触达 adapter。 */
export interface SplatDataCpuPicker extends Disposable {
  pick(query: PickQuery, assets: readonly GSplatSystemAssetSnapshot[]): GSplatPickSample | null
  pickRect?(
    query: RectPickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): readonly GSplatPickSample[]
}

/** CPU/GPU picker 统一返回的 backend-neutral 命中样本。 */
export interface GSplatPickSample {
  readonly assetId: AssetId
  readonly resourceGeneration: number
  readonly point: THREE.Vector3
  readonly distance?: number
  readonly normal?: THREE.Vector3
  readonly raw?: unknown
}

/**
 * System-owned GPU pick 编排器。
 * adapter 只创建 target/pass/readback leaf，不拥有请求队列、快照或结果映射。
 */
export interface GSplatGpuPickCoordinator extends Disposable {
  /** WebGL 可选同步 GPU path；WebGPU 不得以 busy-wait 实现该入口。 */
  pickSync?(query: PickQuery, assets: readonly GSplatSystemAssetSnapshot[]): GSplatPickSample | null
  /** WebGL 可选同步矩形 GPU path；WebGPU 不得以 busy-wait 实现该入口。 */
  pickRectSync?(
    query: RectPickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): readonly GSplatPickSample[]
  /** 可选的 GPU picker 预热；只允许准备私有资源，不向调用方隐式返回 readback。 */
  prewarm?(query: PickQuery, assets: readonly GSplatSystemAssetSnapshot[]): void
  request(
    query: PickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): Promise<GSplatPickSample | null>
  /** 异步 GPU 矩形拾取；WebGPU 使用同一私有 ID pass/readback 队列。 */
  requestRect?(
    query: RectPickQuery,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): Promise<readonly GSplatPickSample[]>
  /** MainTransparent forward 提交完成后，由 System 驱动一次私有 pick pass。 */
  renderView?(
    frame: ViewFrameContext,
    context: PhaseRenderContext,
    assets: readonly GSplatSystemAssetSnapshot[]
  ): void
  prepareView(
    frame: ViewFrameContext,
    assets: readonly GSplatSystemAssetSnapshot[],
    lifecycle: GSplatViewLifecycleContext
  ): void
  cancelAsset(id: AssetId, generation: number): void
  cancelView(viewId: ViewId, generation: number): void
}

export interface GSplatSystemOptions {
  readonly adapter: GSplatGraphicsAdapter
  readonly bindingFactory: IGSplatHandleBindingFactory
  readonly director: GSplatSystemDirector
  readonly configuration: GSplatSystemConfiguration
  readonly cpuPicker: SplatDataCpuPicker
  readonly gpuPickCoordinator: GSplatGpuPickCoordinator
  readonly processingSlot?: GSplatProcessingSlot
  readonly onProcessorCommitted?: GSplatProcessorInvalidation
  /** glTF 等容器格式共用的 Unified Resource 构造叶子。 */
  readonly resourceFactory?: GSplatSystemResourceFactory
  readonly loaders?: readonly AssetLoader<GaussianSplatLoadRequest, GSplatResourceLease>[]
  readonly exporters?: readonly AssetExporter<GSplatExportSource, Blob>[]
  /** 迁移期内部 provider；按格式/模式选择，canonical loader 始终优先。 */
  readonly compatibilityMode?: GSplatCompatibilityMode
  readonly compatibilityProviders?: readonly GSplatCompatibilityProvider[]
  readonly createAssetId?: () => AssetId
  /** Unified Params/Director 的唯一控制面；StreamedGSplat 等高层编排器按 owner lease 使用。 */
  readonly runtimeControl?: GSplatSystemRuntimeControl
  /** Plugin-facing configuration/activity services；只允许 System 持有。 */
  readonly hostServices?: GSplatSystemHostServices
  /** 可选的全局视觉活动通知；由 Unified composition 接到 RenderLoop invalidation。 */
  readonly onVisualActivity?: (reason: string, viewId?: ViewId) => void
  /** 当前 adapter 对应的 MainTransparent 提交叶子。 */
  readonly renderSubmission?: GSplatSystemRenderSubmission
}

export interface GSplatSystemResourceFactory {
  readonly graphicsAdapter?: GSplatGraphicsAdapter
  createResource(data: SplatData, context: GSplatResourceCreateContext): GSplatResourceLease
}

export type GSplatSystemRelightingViewControllerOptions = Omit<
  GSplatRelightingViewControllerOptions,
  'adapter' | 'capability' | 'layerRegistry'
>

export interface GSplatSystemProcessorBinding {
  readonly resource?: GSplatResourceLease
  readonly handle?: GaussianSplatHandle
  readonly streams?: readonly string[]
}

interface GSplatSystemGenerator extends Disposable {
  postUpdate?(delta?: number): void
  readonly needsContinuousActivity?: boolean
}

function abortError(): DOMException {
  return new DOMException('Aborted', 'AbortError')
}

function defaultAssetId(): AssetId {
  return globalThis.crypto?.randomUUID?.() ?? `gsplat-${Date.now()}-${Math.random()}`
}

/**
 * WebGL2/WebGPU 共用的唯一 3DGS runtime owner。
 *
 * 此类不读取 adapter.backend 来选择 Runtime/Manager。后端差异只存在于注入的窄 adapter 叶子层。
 */
export class GSplatSystem implements Disposable {
  private readonly adapter: GSplatGraphicsAdapter
  private readonly director: GSplatSystemDirector
  private readonly configuration: GSplatSystemConfiguration
  private readonly processingSlot?: GSplatProcessingSlot
  private readonly onProcessorCommitted?: GSplatProcessorInvalidation
  private readonly assetStore = new GSplatAssetStore()
  private readonly loadingService: GSplatAssetLoadingService
  private readonly handleCommands: GSplatHandleCommandService
  private readonly frameService: GSplatFrameService
  private readonly pickingService: GSplatPickingService
  private readonly flipbookCache: GSplatFlipbookAssetCache
  private readonly runtimeControl?: GSplatSystemRuntimeControl
  private readonly hostServices?: GSplatSystemHostServices
  private readonly onVisualActivity?: (reason: string, viewId?: ViewId) => void
  private readonly renderSubmission?: GSplatSystemRenderSubmission
  private readonly exporters = new ExporterRegistry<GSplatExportSource, Blob>()
  private readonly processors = new Set<GSplatProcessor>()
  private readonly relightingControllers = new Set<GSplatRelightingViewController>()
  private readonly relightingControllersByView = new Map<ViewId, GSplatRelightingViewController>()
  private readonly relightingLayerRegistry = new GSplatRelightingLayerRegistry()
  private readonly treesAttachments = new Set<GSplatTreesAttachment>()
  private readonly treesAttachmentsByAsset = new Map<AssetId, Set<GSplatTreesAttachment>>()
  private readonly treesEffectRefCounts = new Map<GSplatTrees, number>()
  private readonly flipbooks = new Set<GSplatFlipbookController>()
  private readonly generators = new Set<GSplatSystemGenerator>()
  private streamingConfigurationOwner: GSplatSystemOwner | null = null
  private streamingConfigurationRelease: (() => void) | null = null
  private debugOwner: GSplatSystemOwner | null = null
  private debugRelease: (() => void) | null = null
  private lastControllerFrameNumber = -1
  private disposed = false

  constructor(options: GSplatSystemOptions) {
    this.adapter = options.adapter
    this.director = options.director
    this.configuration = options.configuration
    this.processingSlot = options.processingSlot
    this.onProcessorCommitted = options.onProcessorCommitted
    this.runtimeControl = options.runtimeControl
    this.hostServices = options.hostServices
    this.onVisualActivity = options.onVisualActivity
    this.renderSubmission = options.renderSubmission
    this.loadingService = new GSplatAssetLoadingService(
      options.adapter,
      options.configuration,
      options.resourceFactory,
      options.compatibilityMode,
      options.compatibilityProviders
    )
    this.pickingService = new GSplatPickingService(
      this.assetStore,
      options.configuration,
      options.adapter,
      options.cpuPicker,
      options.gpuPickCoordinator
    )
    this.handleCommands = new GSplatHandleCommandService(
      this.assetStore,
      options.bindingFactory,
      options.createAssetId ?? defaultAssetId,
      {
        retireGeneration: (id, generation) => this.pickingService.cancelAsset(id, generation),
      }
    )
    this.frameService = new GSplatFrameService(
      this.assetStore,
      options.director,
      this.pickingService,
      options.adapter
    )
    this.flipbookCache = new GSplatFlipbookAssetCache(this)

    for (const loader of options.loaders ?? []) this.loadingService.register(loader)
    for (const exporter of options.exporters ?? []) this.registerExporter(exporter)
  }

  /** 当前 Engine 唯一的 GSplat GPU leaf adapter。 */
  get graphicsAdapter(): GSplatGraphicsAdapter {
    return this.adapter
  }

  /** 当前 format/adapter/renderer selection 解析出的稳定能力快照。 */
  get capabilities(): GSplatCapability {
    return this.configuration.capabilities
  }

  ensureExtraStreams(streams: readonly GSplatStreamDescriptor[]): boolean {
    this.assertActive()
    const host = this.requireHostServices('ensureExtraStreams')
    if (!host.ensureExtraStreams) {
      throw new Error('GSplatSystem: 当前 backend 不支持 Unified extra stream transaction')
    }
    return host.ensureExtraStreams(streams)
  }

  /**
   * Attach a Trees copy/render effect to a real Unified placement.
   *
   * Compatibility bindings intentionally do not expose placement parameters, so
   * they fail at this stable boundary instead of silently affecting only the
   * final material.
   */
  attachTrees(id: AssetId, trees: GSplatTrees): GSplatTreesAttachment {
    this.assertActive()
    const record = this.assetStore.get(id)
    if (!record) throw new Error(`GSplatSystem: Trees 未找到资产 "${id}"`)
    const binding = record.binding
    if (
      !binding.setParameter ||
      !binding.getParameter ||
      !binding.deleteParameter ||
      !binding.setWorkBufferModifier ||
      !binding.getShaderEffect
    ) {
      throw new UnsupportedGraphicsCapabilityError(
        'gsplat-trees-placement-parameters',
        this.adapter.backend
      )
    }
    const currentEffect = binding.getShaderEffect()
    if (currentEffect && currentEffect !== trees) {
      throw new UnsupportedGraphicsCapabilityError(
        'gsplat-trees-shared-effect-owner',
        this.adapter.backend,
        '当前 Unified shared material 已由另一个 shader effect owner 持有'
      )
    }

    const target: GSplatTreesPlacementTarget = {
      setParameter: binding.setParameter.bind(binding),
      getParameter: binding.getParameter.bind(binding),
      deleteParameter: binding.deleteParameter.bind(binding),
      setWorkBufferModifier: binding.setWorkBufferModifier.bind(binding),
      setWorkBufferUpdateMode: (mode) => {
        if (!('workBufferUpdateMode' in binding)) {
          throw new Error('GSplatSystem: Trees target 缺少 WorkBuffer update mode')
        }
        ;(
          binding as IGSplatHandleBinding & { workBufferUpdateMode: GSplatWorkBufferUpdateMode }
        ).workBufferUpdateMode = mode
      },
    }
    const attachment = attachGSplatTrees({
      trees,
      adapter: this.adapter,
      target,
      publishFormat: (streams) => this.ensureExtraStreams(streams),
      label: `gsplat-trees:${id}`,
    })
    try {
      if (!binding.handle.setShaderEffect(trees)) {
        throw new UnsupportedGraphicsCapabilityError(
          'gsplat-trees-shader-effect',
          this.adapter.backend,
          '当前 Handle 未能提交 Trees render effect'
        )
      }
    } catch (error) {
      attachment.dispose()
      throw error
    }
    this.treesEffectRefCounts.set(trees, (this.treesEffectRefCounts.get(trees) ?? 0) + 1)
    const releaseTreesSubscription = trees.subscribe(() =>
      this.onVisualActivity?.('gsplat.trees', id)
    )
    const managed: GSplatTreesAttachment = {
      get resources() {
        return attachment.resources
      },
      rebuild: () => attachment.rebuild(),
      dispose: () => {
        if (!this.treesAttachments.delete(managed)) return
        const assetAttachments = this.treesAttachmentsByAsset.get(id)
        assetAttachments?.delete(managed)
        if (assetAttachments?.size === 0) this.treesAttachmentsByAsset.delete(id)
        const effectRefCount = (this.treesEffectRefCounts.get(trees) ?? 1) - 1
        if (effectRefCount > 0) {
          this.treesEffectRefCounts.set(trees, effectRefCount)
        } else {
          this.treesEffectRefCounts.delete(trees)
          if (binding.getShaderEffect?.() === trees) binding.handle.clearShaderEffect()
        }
        releaseTreesSubscription()
        attachment.dispose()
      },
    }
    this.treesAttachments.add(managed)
    let assetAttachments = this.treesAttachmentsByAsset.get(id)
    if (!assetAttachments) {
      assetAttachments = new Set()
      this.treesAttachmentsByAsset.set(id, assetAttachments)
    }
    assetAttachments.add(managed)
    return managed
  }

  /**
   * 创建 System-owned per-View Relighting controller。
   *
   * 这里仅把 adapter/capability/named-layer registry 收敛到唯一 GSplatSystem；实际 initialize/update/render
   * 仍由调用方在目标 View 的稳定帧边界驱动，避免隐式改变现有 forward 链路。
   */
  createRelightingViewController(
    options: GSplatSystemRelightingViewControllerOptions
  ): GSplatRelightingViewController {
    this.assertActive()
    for (const existing of this.relightingControllers) {
      if (existing.viewId === options.viewId) {
        throw new Error(`GSplatRelighting View controller 已存在: ${options.viewId}`)
      }
    }
    const controller = new GSplatRelightingViewController({
      ...options,
      adapter: this.adapter,
      capability: this.configuration.capabilities.relighting,
      layerRegistry: this.relightingLayerRegistry,
      onInvalidate: (viewId) => {
        options.onInvalidate?.(viewId)
        this.onVisualActivity?.('gsplat.relighting', viewId)
      },
    })
    this.relightingControllers.add(controller)
    this.relightingControllersByView.set(options.viewId, controller)
    return controller
  }

  disposeRelightingViewController(controller: GSplatRelightingViewController): boolean {
    this.assertActive()
    if (!this.relightingControllers.delete(controller)) return false
    if (this.relightingControllersByView.get(controller.viewId) === controller) {
      this.relightingControllersByView.delete(controller.viewId)
    }
    controller.destroy()
    return true
  }

  registerLoader(loader: AssetLoader<GaussianSplatLoadRequest, GSplatResourceLease>): void {
    this.assertActive()
    this.loadingService.register(loader)
  }

  registerExporter(exporter: AssetExporter<GSplatExportSource, Blob>): void {
    this.assertActive()
    this.exporters.register(exporter)
  }

  get loaderFormats(): readonly string[] {
    return Object.freeze(this.loadingService.formats())
  }

  /** 可信 splatCount 后、CPU 全量展开和 GPU 分配前执行统一硬上限门禁。 */
  assertResourceCount(numSplats: number): void {
    this.assertActive()
    this.loadingService.assertResourceCount(numSplats)
  }

  /** KHR_gaussian_splatting 与普通 Loader 共用的 backend-neutral Resource factory。 */
  createResource(data: SplatData, signal?: AbortSignal): GSplatResourceLease {
    this.assertActive()
    return this.loadingService.createResource(data, signal)
  }

  /** Flipbook/procedural controller 共用的 Resource-only 加载入口，不创建第二套 registry 或 Handle。 */
  async loadResource(request: Readonly<GaussianSplatLoadRequest>): Promise<GSplatResourceLease> {
    this.assertActive()
    if (request.signal?.aborted) throw abortError()
    if (this.configuration.getResolvedSortingMode() === 'gpu') {
      assertGSplatGpuSortingRequirements(this.capabilities, this.adapter.backend)
    }
    const loader = this.loadingService.resolve(request)
    const signal = request.signal
    let rejectAbort: ((reason: DOMException) => void) | undefined
    const abortPromise = new Promise<never>((_resolve, reject) => {
      rejectAbort = reject
    })
    const onAbort = () => rejectAbort?.(abortError())
    signal?.addEventListener('abort', onAbort, { once: true })
    if (signal?.aborted) onAbort()

    try {
      const loaderPromise = loader.load(request, { signal }).then((resource) => {
        if (this.disposed || signal?.aborted) {
          resource.dispose()
          throw abortError()
        }
        try {
          this.loadingService.assertResourceAdapter(resource)
        } catch (error) {
          resource.dispose()
          throw error
        }
        return resource
      })
      return await Promise.race([loaderPromise, abortPromise])
    } finally {
      signal?.removeEventListener('abort', onAbort)
    }
  }

  /** 以 direct Resource 创建公共稳定 Handle；无论成功失败都会消费输入 lease。 */
  createWithResource(
    request: Readonly<GaussianSplatLoadRequest>,
    resource: GSplatResourceLease
  ): GaussianSplatHandle {
    let submitted = false
    try {
      this.assertActive()
      this.loadingService.assertResourceAdapter(resource)
      const id = request.id ?? this.handleCommands.createId()
      if (this.assetStore.isReserved(id)) {
        throw new Error(`GSplatSystem: asset id "${id}" 已存在或正在加载`)
      }
      if (this.configuration.getResolvedSortingMode() === 'gpu') {
        assertGSplatGpuSortingRequirements(this.capabilities, this.adapter.backend)
      }
      const normalizedRequest = Object.freeze({ ...request, id, signal: undefined })
      submitted = true
      return this.handleCommands.create(
        id,
        normalizedRequest,
        resource,
        this.assetStore.allocateGeneration(),
        { kind: 'direct' }
      )
    } catch (error) {
      if (!submitted) resource.dispose()
      throw error
    }
  }

  /** 创建并立即启动一个由当前 System/FrameDriver 拥有的 Flipbook controller。 */
  createFlipbook(options: GSplatFlipbookOptions = {}): GSplatFlipbookController {
    this.assertActive()
    const controller = new GSplatFlipbookController(this, this.flipbookCache, options, (disposed) =>
      this.flipbooks.delete(disposed)
    )
    this.flipbooks.add(controller)
    const initialization = controller.initialize()
    // 保留可由调用方 await 的原始 rejecting Promise，同时避免忽略 ready 时产生 unhandled rejection。
    void initialization.catch(() => undefined)
    return controller
  }

  /** 创建一个由当前 System/FrameDriver 拥有的图片 GSplat generator。 */
  createImage(options: GSplatImageOptions = {}): GSplatImage {
    this.assertActive()
    const image = new GSplatImage(this, options, (disposed) => this.generators.delete(disposed))
    this.generators.add(image)
    image.initialize()
    return image
  }

  /** 创建一个由当前 System/FrameDriver 拥有的动态 debug primitive generator。 */
  createLines(options: GSplatLinesOptions = {}): GSplatLines {
    this.assertActive()
    const lines = new GSplatLines(this, options, (disposed) => this.generators.delete(disposed))
    this.generators.add(lines)
    lines.initialize()
    return lines
  }

  /** 创建一个显式 build、由当前 System 拥有生命周期的 mesh surface generator。 */
  createMesh(options: GSplatMeshOptions = {}): GSplatMesh {
    this.assertActive()
    const mesh = new GSplatMesh(this, options, (disposed) => this.generators.delete(disposed))
    this.generators.add(mesh)
    mesh.initialize()
    return mesh
  }

  /** 创建一个由当前 System/FrameDriver 拥有的 Canvas text GSplat generator。 */
  createText(options: GSplatTextOptions = {}): GSplatText {
    this.assertActive()
    const text = new GSplatText(this, options, (disposed) => this.generators.delete(disposed))
    this.generators.add(text)
    text.initialize()
    return text
  }

  /** 创建一个由当前 System/FrameDriver 拥有的程序化 Weather generator。 */
  createWeather(options: GSplatWeatherOptions = {}): GSplatWeatherController {
    this.assertActive()
    const weather = new GSplatWeatherController(this.createWeatherRuntime(), options, (disposed) =>
      this.generators.delete(disposed)
    )
    this.generators.add(weather)
    weather.initialize()
    this.onVisualActivity?.('gsplat.weather')
    return weather
  }

  /** 创建正式 loader/Placement 上的 PlayCanvas StreamedGSplat 高层编排器。 */
  createStreamed(options: Omit<StreamedGSplatOptions, 'system'>): StreamedGSplat {
    this.assertActive()
    const controller = new StreamedGSplat({ ...options, system: this }, (disposed) =>
      this.generators.delete(disposed)
    )
    this.generators.add(controller)
    try {
      const initialization = controller.initialize()
      void initialization.catch(() => undefined)
      return controller
    } catch (error) {
      this.generators.delete(controller)
      controller.dispose()
      throw error
    }
  }

  async load(
    request: GaussianSplatLoadRequest,
    context: Pick<LoadContext, 'onProgress'> = {}
  ): Promise<GaussianSplatHandle> {
    this.assertActive()
    if (request.signal?.aborted) throw abortError()
    const id = request.id ?? this.handleCommands.createId()
    if (this.assetStore.isReserved(id)) {
      throw new Error(`GSplatSystem: asset id "${id}" 已存在或正在加载`)
    }

    if (this.configuration.getResolvedSortingMode() === 'gpu') {
      assertGSplatGpuSortingRequirements(this.capabilities, this.adapter.backend)
    }

    const loader = this.loadingService.resolve(request)
    const pending = this.assetStore.beginPending(
      id,
      Object.freeze({ ...request, id }),
      this.assetStore.allocateGeneration()
    )
    const { controller } = pending

    const normalizedRequest = pending.request
    const forwardAbort = () => controller.abort()
    if (request.signal?.aborted) controller.abort()
    else request.signal?.addEventListener('abort', forwardAbort, { once: true })

    let rejectAbort: ((reason: DOMException) => void) | undefined
    const abortPromise = new Promise<never>((_resolve, reject) => {
      rejectAbort = reject
    })
    const onAbort = () => rejectAbort?.(abortError())
    controller.signal.addEventListener('abort', onAbort, { once: true })
    if (controller.signal.aborted) onAbort()

    const loaderPromise = loader
      .load(normalizedRequest, {
        signal: controller.signal,
        onProgress: context.onProgress,
      })
      .then((resource) => {
        if (!this.assetStore.isCurrent(pending)) {
          resource.dispose()
          throw abortError()
        }
        try {
          this.loadingService.assertResourceAdapter(resource)
        } catch (error) {
          resource.dispose()
          throw error
        }
        return resource
      })

    let resource: GSplatResourceLease | undefined
    try {
      resource = await Promise.race([loaderPromise, abortPromise])
      if (!this.assetStore.isCurrent(pending)) throw abortError()
      const stagedResource = resource
      resource = undefined
      return this.handleCommands.create(id, normalizedRequest, stagedResource, pending.generation)
    } catch (error) {
      resource?.dispose()
      throw error
    } finally {
      controller.signal.removeEventListener('abort', onAbort)
      request.signal?.removeEventListener('abort', forwardAbort)
      this.assetStore.finishPending(pending)
    }
  }

  /**
   * 保留 Handle identity 的 asset reload。旧 Placement 在新 Resource/全部 Manager stage 成功前继续可绘制。
   */
  async replaceAsset(id: AssetId, request: GaussianSplatLoadRequest): Promise<GaussianSplatHandle> {
    this.assertActive()
    const record = this.assetStore.get(id)
    if (!record) throw new Error(`GSplatSystem: replace 未找到资产 "${id}"`)
    if (request.signal?.aborted) throw abortError()
    if (this.configuration.getResolvedSortingMode() === 'gpu') {
      assertGSplatGpuSortingRequirements(this.capabilities, this.adapter.backend)
    }

    const loader = this.loadingService.resolve(request)
    this.assetStore.abortPending(id)
    const pending = this.assetStore.beginPending(
      id,
      Object.freeze({ ...request, id }),
      record.binding.beginResourceReplacement()
    )
    const { controller } = pending
    const forwardAbort = () => controller.abort()
    request.signal?.addEventListener('abort', forwardAbort, { once: true })

    let rejectAbort: ((reason: DOMException) => void) | undefined
    const abortPromise = new Promise<never>((_resolve, reject) => {
      rejectAbort = reject
    })
    const onAbort = () => rejectAbort?.(abortError())
    controller.signal.addEventListener('abort', onAbort, { once: true })

    const loaderPromise = loader
      .load(pending.request, { signal: controller.signal })
      .then((resource) => {
        if (!this.assetStore.isCurrent(pending)) {
          resource.dispose()
          throw abortError()
        }
        try {
          this.loadingService.assertResourceAdapter(resource)
        } catch (error) {
          resource.dispose()
          throw error
        }
        return resource
      })

    let resource: GSplatResourceLease | undefined
    try {
      resource = await Promise.race([loaderPromise, abortPromise])
      const consumedResource = resource
      resource = undefined
      if (!this.assetStore.isCurrent(pending)) {
        consumedResource.dispose()
        throw abortError()
      }
      return this.handleCommands.commitReplacement(
        id,
        pending.generation,
        pending.request,
        consumedResource
      )
    } catch (error) {
      resource?.dispose()
      throw error
    } finally {
      controller.signal.removeEventListener('abort', onAbort)
      request.signal?.removeEventListener('abort', forwardAbort)
      this.assetStore.finishPending(pending)
    }
  }

  /** 非空 direct Resource 优先，并在提交时断开当前 asset source。 */
  replaceWithResource(
    id: AssetId,
    resource: GSplatResourceLease,
    metadata: { readonly url?: string; readonly name?: string } = {}
  ): GaussianSplatHandle {
    let submitted = false
    try {
      this.assertActive()
      this.loadingService.assertResourceAdapter(resource)
      this.assetStore.abortPending(id)
      submitted = true
      return this.handleCommands.replaceWithResource(id, resource, metadata)
    } catch (error) {
      if (!submitted) resource.dispose()
      throw error
    }
  }

  mount(handle: GaussianSplatHandle): void {
    this.assertActive()
    this.handleCommands.mount(handle)
  }

  unmount(id: AssetId): void {
    this.handleCommands.unmount(id)
  }

  setVisibility(id: AssetId, visible: boolean): void {
    this.handleCommands.setVisibility(id, visible)
  }

  /** 对齐 PlayCanvas Entity/Component enabled 边界，不改变 Handle identity。 */
  setAssetEnabled(id: AssetId, enabled: boolean): void {
    this.assertActive()
    const record = this.assetStore.get(id)
    if (!record) throw new Error(`GSplatSystem: enabled 未找到资产 "${id}"`)
    record.binding.setEnabled(enabled)
  }

  /** 原子更新 PlayCanvas GSplatComponent 对应的 per-placement LOD 属性。 */
  setAssetLodConfiguration(id: AssetId, config: Readonly<GSplatAssetLodConfiguration>): void {
    this.assertActive()
    const record = this.assetStore.get(id)
    if (!record) throw new Error(`GSplatSystem: LOD 配置未找到资产 "${id}"`)
    record.binding.setLodConfiguration(config)
  }

  /** Weather/其它程序化 Resource 只通过 System 更新 Placement 参数，避免绕过 binding。 */
  setAssetParameters(id: AssetId, parameters: ReadonlyMap<string, unknown>): void {
    this.assertActive()
    const record = this.assetStore.get(id)
    if (!record) throw new Error(`GSplatSystem: 参数未找到资产 "${id}"`)
    if (!record.binding.setParameters) {
      throw new UnsupportedGraphicsCapabilityError(
        'gsplat-placement-parameters',
        this.adapter.backend,
        '当前 binding 不支持批量 Placement 参数'
      )
    }
    record.binding.setParameters(parameters)
  }

  acquireStreamingConfigurationLease(owner: GSplatSystemOwner): GSplatStreamingConfigurationLease {
    this.assertActive()
    const control = this.requireRuntimeControl()
    if (this.streamingConfigurationOwner !== null) {
      throw new GSplatSystemOwnerConflictError('streaming-configuration')
    }
    const previous = Object.freeze({ ...control.getStreamingConfiguration() })
    let disposed = false
    let dirty = false
    this.streamingConfigurationOwner = owner
    const release = () => {
      if (disposed) return
      disposed = true
      if (this.streamingConfigurationOwner !== owner) return
      this.streamingConfigurationOwner = null
      this.streamingConfigurationRelease = null
      if (dirty) control.setStreamingConfiguration(previous)
    }
    this.streamingConfigurationRelease = release
    return {
      owner,
      set: (config) => {
        if (disposed || this.streamingConfigurationOwner !== owner) {
          throw new Error('GSplatStreamingConfigurationLease 已释放或失效')
        }
        control.setStreamingConfiguration(Object.freeze({ ...config }))
        dirty = true
      },
      dispose: release,
    }
  }

  acquireDebugLease(owner: GSplatSystemOwner): GSplatDebugLease {
    this.assertActive()
    const control = this.requireRuntimeControl()
    if (this.debugOwner !== null) throw new GSplatSystemOwnerConflictError('debug')
    const previous = control.getDebugMode()
    let disposed = false
    let dirty = false
    this.debugOwner = owner
    const release = () => {
      if (disposed) return
      disposed = true
      if (this.debugOwner !== owner) return
      this.debugOwner = null
      this.debugRelease = null
      if (dirty) control.setDebugMode(previous)
    }
    this.debugRelease = release
    return {
      owner,
      set: (mode) => {
        if (disposed || this.debugOwner !== owner) {
          throw new Error('GSplatDebugLease 已释放或失效')
        }
        control.setDebugMode(mode)
        dirty = true
      },
      dispose: release,
    }
  }

  getRenderedSplatCount(): number {
    this.assertActive()
    return Math.max(0, Math.floor(this.requireRuntimeControl().getRenderedSplatCount()))
  }

  getAsset(id: AssetId): GaussianSplatHandle | undefined {
    return this.handleCommands.get(id)
  }

  getAll(): GaussianSplatHandle[] {
    return this.handleCommands.getAll()
  }

  getByUrl(url: string): GaussianSplatHandle | undefined {
    return this.handleCommands.getByUrl(url)
  }

  isLoading(url: string): boolean {
    return this.assetStore.isLoading(url)
  }

  getConfig(): GaussianSplatRenderConfig {
    return this.configuration.getConfig()
  }

  /** capability / cross-Manager transaction 已提交的实际排序管线。 */
  get resolvedSortingMode(): GSplatResolvedSortingMode {
    return this.configuration.getResolvedSortingMode()
  }

  setConfig(config: Partial<GaussianSplatRenderConfig>): void {
    this.assertActive()
    this.configuration.setConfig(config)
  }

  setClipVolumes(volumes: ReadonlyArray<ClipVolume>): boolean {
    this.assertActive()
    return this.requireHostServices('setClipVolumes').setClipVolumes(volumes)
  }

  prewarmClip(maxVertices: number, maxVolumes = 1): boolean {
    this.assertActive()
    return this.requireRuntimeControl().prewarmClip?.(maxVertices, maxVolumes) ?? false
  }

  releaseClipPrewarm(): boolean {
    this.assertActive()
    return this.requireRuntimeControl().releaseClipPrewarm?.() ?? false
  }

  setLodConfig(config: Partial<LODConfig>): void {
    this.assertActive()
    this.requireHostServices('setLodConfig').setLodConfig(config)
  }

  getLodConfig(): LODConfig {
    this.assertActive()
    return this.requireHostServices('getLodConfig').getLodConfig()
  }

  getLodStats(): LODStats {
    this.assertActive()
    return this.requireHostServices('getLodStats').getLodStats()
  }

  getDeviceBudgetDiagnostics(): GSplatDeviceBudgetDiagnostics {
    this.assertActive()
    return this.requireHostServices('getDeviceBudgetDiagnostics').getDeviceBudgetDiagnostics()
  }

  getVisualActivitySnapshot(): GSplatVisualActivitySnapshot | undefined {
    this.assertActive()
    const hostActivity = this.hostServices?.getVisualActivitySnapshot()
    const generatorNeedsFrame = [...this.generators].some(
      (generator) => generator.needsContinuousActivity === true
    )
    if (!hostActivity && !generatorNeedsFrame) return undefined
    if (!generatorNeedsFrame) return hostActivity
    return {
      needsNextFrame: true,
      reasons: [...new Set([...(hostActivity?.reasons ?? []), 'gsplat.generator'])],
      ...(hostActivity?.viewIds ? { viewIds: hostActivity.viewIds } : {}),
    }
  }

  /** 对齐 ComponentSystem clone：复制 binding 状态，但获取独立 Resource lease 与 picker ID。 */
  cloneAsset(
    sourceId: AssetId,
    cloneId: AssetId = this.handleCommands.createId()
  ): GaussianSplatHandle {
    this.assertActive()
    return this.handleCommands.clone(sourceId, cloneId)
  }

  /** 由唯一 System 创建 Unified processor；真正执行必须进入 Prepare processing slot。 */
  createProcessor(
    source: GSplatSystemProcessorBinding,
    destination: GSplatSystemProcessorBinding,
    options: GSplatProcessorOptions
  ): GSplatProcessor {
    this.assertActive()
    if (!this.processingSlot) {
      throw new Error('GSplatSystem: 未配置 Prepare processing slot')
    }
    const processor = new GSplatProcessor(
      this.adapter,
      this.processingSlot,
      this.handleCommands.resolveProcessorBinding(source),
      this.handleCommands.resolveProcessorBinding(destination),
      options,
      this.onProcessorCommitted
    )
    this.processors.add(processor)
    return processor
  }

  getMaterial(viewId: ViewId, layerId: number): unknown | null {
    this.assertActive()
    return this.frameService.getMaterial(viewId, layerId)
  }

  /** FrameDriver Update phase：同一 logical frame 最多成功推进一次 streaming。 */
  updateFrame(frame: EngineFrameContext): void {
    this.assertActive()
    if (this.lastControllerFrameNumber !== frame.frameNumber) {
      for (const controller of [...this.flipbooks]) controller.update(frame.delta)
      for (const generator of [...this.generators]) generator.postUpdate?.(frame.delta)
      this.flipbookCache.processRetired()
      this.lastControllerFrameNumber = frame.frameNumber
    }
    this.frameService.update(frame)
    this.syncRelightingViewLifecycle(frame.views)
  }

  /** RenderPipeline Prepare：只准备目标 View，不创建 backend-specific System。 */
  prepareView(frame: ViewFrameContext): void {
    this.assertActive()
    this.frameService.prepare(frame)
    const controller = this.relightingControllersByView.get(frame.viewId)
    if (!controller?.enabled) return
    const surface = frame.graphics?.surface
    if (!surface) return
    const surfaceWidth =
      surface.drawingBufferWidth > 0 ? surface.drawingBufferWidth : surface.logicalWidth
    const surfaceHeight =
      surface.drawingBufferHeight > 0 ? surface.drawingBufferHeight : surface.logicalHeight
    const scaleX = surface.logicalWidth > 0 ? surfaceWidth / surface.logicalWidth : 1
    const scaleY = surface.logicalHeight > 0 ? surfaceHeight / surface.logicalHeight : 1
    controller.updateFrame({
      camera: frame.camera,
      surfaceWidth,
      surfaceHeight,
      viewRect: {
        x: frame.viewRect.x * scaleX,
        y: frame.viewRect.y * scaleY,
        width: frame.viewRect.width * scaleX,
        height: frame.viewRect.height * scaleY,
      },
    })
  }

  renderView(frame: ViewFrameContext, context: PhaseRenderContext): void {
    this.assertActive()
    const assets = this.assetStore.snapshot()
    this.relightingControllersByView.get(frame.viewId)?.render()
    this.renderSubmission?.render(frame, context, assets)
    // The picker reuses the Hybrid projector/projection-cache resources. Keep it after the
    // forward submission: pick mode stores IDs in cache slots that the forward shader interprets
    // as color. Running the pick preparation first would leak encoded IDs into the visible pass.
    this.pickingService.renderView(frame, context, assets)
  }

  pickCpu(query: PickQuery): PickResult | null {
    this.assertActive()
    return this.pickingService.pickCpu(query)
  }

  /** 同步普通入口：WebGL GPU-first，GPU 不可用/无命中时回退 CPU；WebGPU 保持 CPU 语义。 */
  pick(query: PickQuery): PickResult | null {
    this.assertActive()
    return this.pickingService.pick(query)
  }

  async pickGpu(query: PickQuery): Promise<PickResult | null> {
    this.assertActive()
    const result = await this.pickingService.pickGpu(query)
    return this.disposed ? null : result
  }

  pickRect(query: RectPickQuery): PickResult[] {
    this.assertActive()
    return this.pickingService.pickRect(query)
  }

  async pickRectGpu(query: RectPickQuery): Promise<PickResult[]> {
    this.assertActive()
    const result = await this.pickingService.pickRectGpu(query)
    return this.disposed ? [] : result
  }

  async export(id: AssetId, format: string, options?: ExportOptions): Promise<Blob> {
    this.assertActive()
    const record = this.assetStore.get(id)
    if (!record) throw new Error(`GSplatSystem: export 未找到资产 "${id}"`)
    const exporter = this.exporters.get(format)
    if (!exporter) {
      throw new Error(
        `GSplatSystem: 不支持导出格式 "${format}"，已注册格式: ${this.exporters.formats().join(', ')}`
      )
    }
    const source: GSplatExportSource = Object.freeze({
      handle: record.binding.handle,
      resource: record.binding.resource,
      cpuData: record.binding.resource.getCpuExportData(),
      resourceKind: record.binding.resource.resourceKind ?? 'unknown',
      streaming: record.binding.resource.streaming ?? 'static',
      octreeSemantics: record.binding.resource.octreeSemantics ?? 'none',
    })
    assertGSplatExtraExporterCapability(
      exporter as unknown as GaussianSplatExtraExporter,
      this.adapter.backend,
      source
    )
    if (exporter.canExport && !exporter.canExport(source)) {
      throw new Error(`GSplatSystem: 资产 "${id}" 不支持导出为 "${format}"`)
    }
    return exporter.export(source, options)
  }

  disposeAsset(id: AssetId): boolean {
    const existed = this.assetStore.has(id)
    for (const attachment of [...(this.treesAttachmentsByAsset.get(id) ?? [])]) {
      attachment.dispose()
    }
    this.assetStore.abortPending(id)
    this.handleCommands.disposeAsset(id)
    return existed
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    let firstError: unknown
    const disposeStep = (operation: () => void) => {
      try {
        operation()
      } catch (error) {
        firstError ??= error
      }
    }
    this.assetStore.close()
    for (const attachment of [...this.treesAttachments].reverse()) {
      disposeStep(() => attachment.dispose())
    }
    this.treesAttachments.clear()
    this.treesAttachmentsByAsset.clear()
    this.treesEffectRefCounts.clear()
    for (const generator of [...this.generators].reverse()) {
      disposeStep(() => generator.dispose())
    }
    this.generators.clear()
    disposeStep(() => this.debugRelease?.())
    disposeStep(() => this.streamingConfigurationRelease?.())
    for (const controller of [...this.flipbooks].reverse()) {
      disposeStep(() => controller.dispose())
    }
    this.flipbooks.clear()
    disposeStep(() => this.flipbookCache.dispose())
    disposeStep(() => this.frameService.dispose())
    disposeStep(() => this.pickingService.dispose())

    for (const processor of [...this.processors].reverse()) {
      disposeStep(() => processor.dispose())
    }
    this.processors.clear()

    for (const controller of [...this.relightingControllers].reverse()) {
      disposeStep(() => controller.destroy())
    }
    this.relightingControllers.clear()
    this.relightingControllersByView.clear()

    const records = this.assetStore.values().reverse()
    for (const record of records) {
      if (record.binding.mounted) disposeStep(() => record.binding.unmount())
      disposeStep(() => record.binding.dispose())
    }
    disposeStep(() => this.director.dispose())
    disposeStep(() => this.renderSubmission?.dispose())
    disposeStep(() => this.hostServices?.dispose())
    this.assetStore.clear()
    disposeStep(() => this.adapter.dispose())
    if (firstError) throw firstError
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('GSplatSystem 已销毁')
  }

  private syncRelightingViewLifecycle(views: readonly ViewSnapshot[]): void {
    const snapshots = new Map(views.map((view) => [view.id, view]))
    for (const controller of [...this.relightingControllers]) {
      const snapshot = snapshots.get(controller.viewId)
      if (!snapshot) {
        this.disposeRelightingViewController(controller)
        continue
      }
      if (
        controller.enabled &&
        (!snapshot.visible || snapshot.rect.width <= 0 || snapshot.rect.height <= 0)
      ) {
        controller.disable()
      }
    }
  }

  private requireRuntimeControl(): GSplatSystemRuntimeControl {
    if (!this.runtimeControl) {
      throw new Error('GSplatSystem: 未配置 Unified runtime control')
    }
    return this.runtimeControl
  }

  private requireHostServices(operation: string): GSplatSystemHostServices {
    if (!this.hostServices) {
      throw new Error(`GSplatSystem: ${operation} 未配置 host services`)
    }
    return this.hostServices
  }

  private createWeatherRuntime(): GSplatWeatherRuntime {
    return {
      graphicsAdapter: this.adapter,
      onVisualActivity: (reason) => this.onVisualActivity?.(reason),
      createWithResource: (request, resource) => this.createWithResource(request, resource),
      replaceWithResource: (id, resource, metadata) =>
        this.replaceWithResource(id, resource, metadata),
      mount: (handle) => this.mount(handle),
      disposeAsset: (id) => this.disposeAsset(id),
      setParameters: (id, parameters) => this.setAssetParameters(id, parameters),
      assertResourceCount: (count) => this.assertResourceCount(count),
    }
  }
}
